import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function OfficialDiscountsPage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const discounts = [
    {
      name: "Military Discount",
      percent: "25%",
      description: "Available to all verified service members."
    },
    {
      name: "Employee Discount", 
      percent: "25%",
      description: "Available to all Gun Store Employees."
    },
    {
      name: "Law Enforcement Discount",
      percent: "20%", 
      description: "Available to active duty police officers and federal agents."
    },
    {
      name: "First Responder Discount",
      percent: "15%",
      description: "Available to firefighters, EMTs, and paramedics."
    },
    {
      name: "Veteran Discount",
      percent: "20%",
      description: "Available to all honorably discharged veterans."
    },
    {
      name: "Bulk Purchase Discount",
      percent: "10%",
      description: "Available for orders over $5,000. Contact for details."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white">
      {/* Header */}
      <header className="relative z-50">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            {/* Logo and Brand */}
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-14 h-14 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/dcd65f13a790094d0aa5adf5c5f61db5/d92e14626dcee2662ced5b45bc6a77f4.png"
                  alt="Tactical Arms Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Tactical Arms</h1>
                <p className="text-sm text-gray-300">NXT Cam Properties • Firearms • Ammunition • Tactical Gear</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              <Link to="/" className="text-gray-300 hover:text-white transition-colors">
                Home
              </Link>
              <Link to="/official-discounts" className="text-yellow-400 font-medium border-b-2 border-yellow-400 pb-1">
                Official Discounts
              </Link>
              <Link to="/shop" className="text-gray-300 hover:text-white transition-colors">
                Shop
              </Link>
              <Link to="/employee-application" className="text-gray-300 hover:text-white transition-colors">
                Employee Application
              </Link>
              <Link to="/employment-list" className="text-gray-300 hover:text-white transition-colors">
                Employment List
              </Link>
              <div className="relative group">
                <button className="text-gray-300 hover:text-white transition-colors flex items-center space-x-1">
                  <span>Blacklists</span>
                  <i className="ri-arrow-down-s-line"></i>
                </button>
                <div className="absolute top-full left-0 mt-2 w-64 bg-gray-800 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="py-2">
                    <Link to="/blacklists/player-blacklists" className="block px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors">
                      Player Blacklists
                    </Link>
                    <Link to="/blacklists/business-mafia-blacklists" className="block px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors">
                      Business/Mafia Blacklists
                    </Link>
                  </div>
                </div>
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-white hover:text-yellow-400 transition-colors"
            >
              <i className={`text-xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="lg:hidden mt-4 bg-gray-800 rounded-lg p-4">
              <nav className="flex flex-col space-y-3">
                <Link to="/" className="text-gray-300 hover:text-white transition-colors py-2">
                  Home
                </Link>
                <Link to="/official-discounts" className="text-yellow-400 font-medium py-2">
                  Official Discounts
                </Link>
                <Link to="/shop" className="text-gray-300 hover:text-white transition-colors py-2">
                  Shop
                </Link>
                <Link to="/employee-application" className="text-gray-300 hover:text-white transition-colors py-2">
                  Employee Application
                </Link>
                <Link to="/employment-list" className="text-gray-300 hover:text-white transition-colors py-2">
                  Employment List
                </Link>
                <div className="border-t border-gray-700 pt-3 mt-3">
                  <p className="text-gray-400 text-sm mb-2">Blacklists</p>
                  <Link to="/blacklists/player-blacklists" className="text-gray-300 hover:text-white transition-colors py-2 pl-4 block">
                    Player Blacklists
                  </Link>
                  <Link to="/blacklists/business-mafia-blacklists" className="text-gray-300 hover:text-white transition-colors py-2 pl-4 block">
                    Business/Mafia Blacklists
                  </Link>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-yellow-400 mb-6">Active Discounts</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Tactical Arms is proud to offer special discounts to those who serve our communities and country. 
              Verify your eligibility to receive exclusive pricing on our premium firearms, ammunition, and tactical gear.
            </p>
          </div>

          {/* Discounts Table */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-yellow-400/30 overflow-hidden shadow-2xl">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-900/80 border-b-2 border-yellow-400">
                    <th className="text-left py-6 px-8 text-yellow-400 font-bold text-lg">
                      Discount Name
                    </th>
                    <th className="text-left py-6 px-8 text-yellow-400 font-bold text-lg">
                      Percent
                    </th>
                    <th className="text-left py-6 px-8 text-yellow-400 font-bold text-lg">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {discounts.map((discount, index) => (
                    <tr 
                      key={index}
                      className={`border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors ${
                        index % 2 === 0 ? 'bg-gray-800/20' : 'bg-gray-900/20'
                      }`}
                    >
                      <td className="py-6 px-8">
                        <span className="text-white font-semibold text-lg">
                          {discount.name}
                        </span>
                      </td>
                      <td className="py-6 px-8">
                        <span className="text-yellow-400 font-bold text-xl">
                          {discount.percent}
                        </span>
                      </td>
                      <td className="py-6 px-8">
                        <span className="text-gray-300 italic leading-relaxed">
                          {discount.description}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Verification Process */}
          <div className="mt-16 grid md:grid-cols-2 gap-8">
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 border border-gray-700/50">
              <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Verification Required</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                All discounts require proper verification of eligibility. Please bring valid identification 
                and proof of service/employment when visiting our store or contact us for online verification procedures.
              </p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center">
                  <i className="ri-check-line text-yellow-400 mr-2"></i>
                  Valid government-issued ID
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-yellow-400 mr-2"></i>
                  Military/Service ID or credentials
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-yellow-400 mr-2"></i>
                  Employment verification (if applicable)
                </li>
              </ul>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 border border-gray-700/50">
              <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-information-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Important Notes</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                Please review our discount policies and terms before making your purchase. 
                Some restrictions may apply to certain products or manufacturers.
              </p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center">
                  <i className="ri-arrow-right-s-line text-yellow-400 mr-2"></i>
                  Cannot be combined with other offers
                </li>
                <li className="flex items-center">
                  <i className="ri-arrow-right-s-line text-yellow-400 mr-2"></i>
                  Valid on in-stock items only
                </li>
                <li className="flex items-center">
                  <i className="ri-arrow-right-s-line text-yellow-400 mr-2"></i>
                  Subject to manufacturer restrictions
                </li>
                <li className="flex items-center">
                  <i className="ri-arrow-right-s-line text-yellow-400 mr-2"></i>
                  Verification required for each purchase
                </li>
              </ul>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center bg-gradient-to-r from-yellow-600 to-yellow-500 rounded-xl p-12">
            <h3 className="text-3xl font-bold text-black mb-4">
              Ready to Save on Premium Equipment?
            </h3>
            <p className="text-xl text-black/80 mb-8 max-w-2xl mx-auto">
              Visit our store or contact us to verify your eligibility and start saving on the best firearms, 
              ammunition, and tactical gear available.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/shop" 
                className="bg-black text-yellow-400 hover:bg-gray-900 font-bold py-4 px-8 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                Browse Products
              </Link>
              <Link 
                to="/employee-application" 
                className="border-2 border-black text-black hover:bg-black hover:text-yellow-400 font-bold py-4 px-8 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                Apply for Employee Discount
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black py-12 mt-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center">
                  <img 
                    src="https://static.readdy.ai/image/dcd65f13a790094d0aa5adf5c5f61db5/d92e14626dcee2662ced5b45bc6a77f4.png"
                    alt="Tactical Arms Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <span className="text-xl font-bold text-white">Tactical Arms</span>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Your trusted source for premium firearms, ammunition, and tactical equipment. A division of NXT Cam Properties.
              </p>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/shop" className="text-gray-400 hover:text-yellow-400 transition-colors">Shop</Link></li>
                <li><Link to="/official-discounts" className="text-gray-400 hover:text-yellow-400 transition-colors">Discounts</Link></li>
                <li><Link to="/employee-application" className="text-gray-400 hover:text-yellow-400 transition-colors">Careers</Link></li>
                <li><Link to="/employment-list" className="text-gray-400 hover:text-yellow-400 transition-colors">Employment</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4">Blacklists</h4>
              <ul className="space-y-2">
                <li><Link to="/blacklists/player-blacklists" className="text-gray-400 hover:text-yellow-400 transition-colors">Player Blacklists</Link></li>
                <li><Link to="/blacklists/business-mafia-blacklists" className="text-gray-400 hover:text-yellow-400 transition-colors">Business/Mafia Blacklists</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4">Contact Info</h4>
              <div className="space-y-2 text-gray-400">
                <p>Licensed Firearms Dealer</p>
                <p>Professional Grade Equipment</p>
                <p>NXT Cam Properties</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Tactical Arms - NXT Cam Properties. All rights reserved.
            </p>
            <div className="flex items-center space-x-4 mt-4 md:mt-0">
              <a href="https://readdy.ai/?origin=logo" className="text-gray-400 hover:text-yellow-400 transition-colors text-sm">
                Website Builder
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}